import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useGoogleAuthMutation } from '../../store/api/authApi';
import { setPendingAuth } from '../../store/slices/authSlice';

/**
 * Google sign-in, wired to the app's normal login flow.
 *
 * WHY THIS RENDERS GOOGLE'S OWN BUTTON
 *
 * The previous version drew the app's styled button and laid Google's real one
 * on top at opacity 0, so the click landed on Google's. That could not be made
 * to line up reliably: Google renders its button inside a cross-origin iframe at
 * its own intrinsic size, and forcing the wrapper to 100% does not resize the
 * clickable region inside it. The result was a hit area smaller than the button
 * the user could see, so parts of it did nothing -- and the whole arrangement
 * depended on Google's internal DOM, which they are free to change at any time.
 *
 * Google's button is now the real, visible control. It is configured to match
 * the app's pill buttons as closely as the API allows (outline theme, pill
 * shape, large size, "Continue with" text), and it is sized to its container and
 * kept in sync on resize. This also keeps the page within Google's sign-in
 * branding guidelines, which the disguised-overlay approach did not.
 *
 * On success the backend does NOT return a session token -- it sends a login
 * OTP, exactly as a password login does -- so this routes to /verify-otp. A
 * trusted device short-circuits with a token and goes straight on, and a 2FA
 * account is handed to the authenticator step, both matching the password flow.
 */

// Google clamps the rendered button to this range; asking for more is ignored,
// so the container width is clamped here to keep our layout and theirs in step.
const GSI_MIN_WIDTH = 200;
const GSI_MAX_WIDTH = 400;

const GoogleSignInButton = ({ onError }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [googleAuth] = useGoogleAuthMutation();
  const mountRef = useRef(null);
  const [unavailable, setUnavailable] = useState(false);

  const handleGoogleResponse = useCallback(async (response) => {
    try {
      const res = await googleAuth({ credential: response.credential }).unwrap();
      const data = res?.data || {};

      // 2FA account on a trusted device: straight to the authenticator step.
      if (data.twoFactorRequired) {
        navigate('/verify-otp', { state: { pendingToken: data.pendingToken } });
        return;
      }

      // Trusted device: authSlice already stored the token from this response.
      if (data.token) {
        navigate('/onboarding');
        return;
      }

      // Normal path: an OTP is on its way to the Google-verified address. Hand
      // over to the same /verify-otp screen the password login uses.
      dispatch(setPendingAuth({ email: data.email, type: 'login' }));
      navigate('/verify-otp');
    } catch (err) {
      onError?.(err.data?.message || 'Google sign-in failed. Please try again.');
    }
  }, [googleAuth, navigate, dispatch, onError]);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

    if (!clientId || clientId.startsWith('your-google-client-id')) {
      console.warn(
        '[GoogleSignIn] REACT_APP_GOOGLE_CLIENT_ID is not configured, so the Google button cannot render.'
      );
      setUnavailable(true);
      return undefined;
    }

    let cancelled = false;
    let observer = null;

    const draw = () => {
      const mount = mountRef.current;
      if (cancelled || !window.google?.accounts?.id || !mount) return;

      // Width has to be an explicit number: Google's button does not stretch to
      // a percentage container, which is what left the old overlay narrower
      // than the button behind it.
      const width = Math.round(
        Math.min(GSI_MAX_WIDTH, Math.max(GSI_MIN_WIDTH, mount.offsetWidth || GSI_MIN_WIDTH)),
      );

      // renderButton appends, so clear first or a resize stacks buttons.
      mount.innerHTML = '';
      window.google.accounts.id.renderButton(mount, {
        theme: 'outline',
        size: 'large',
        type: 'standard',
        shape: 'pill',
        text: 'continue_with',
        logo_alignment: 'left',
        width,
      });
    };

    const start = () => {
      if (cancelled || !window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleResponse,
      });
      draw();

      // Keep the rendered width in step with the container across breakpoints.
      if (mountRef.current && typeof ResizeObserver !== 'undefined') {
        let last = mountRef.current.offsetWidth;
        observer = new ResizeObserver(() => {
          const next = mountRef.current?.offsetWidth || 0;
          // Redraw only on a real change: renderButton is not cheap, and the
          // observer fires for the size change it causes itself.
          if (Math.abs(next - last) > 4) {
            last = next;
            draw();
          }
        });
        observer.observe(mountRef.current);
      }
    };

    const existing = document.querySelector(
      'script[src="https://accounts.google.com/gsi/client"]',
    );

    if (existing) {
      if (window.google?.accounts?.id) start();
      else existing.addEventListener('load', start, { once: true });
    } else {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.addEventListener('load', start, { once: true });
      script.addEventListener('error', () => {
        if (!cancelled) setUnavailable(true);
      }, { once: true });
      document.body.appendChild(script);
    }

    // The script is shared across mounts, so it stays; only this instance's
    // pending work is cancelled.
    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [handleGoogleResponse]);

  // Nothing is rendered when Google cannot load, rather than an inert button
  // that looks clickable and is not.
  if (unavailable) return null;

  return <div className="google-gsi-mount" ref={mountRef} />;
};

export default GoogleSignInButton;
